import { useState } from 'react';

import { CloseButton, TextInput, TextInputProps } from '@any-ui-react/core';
import { useUncontrolled } from '@mantine/hooks';
import { isURL } from 'validator';

import { TrackingUrl } from '@anyx/feature-crm/shared';


import { TrackingLinkDisplay } from '../tracking-link-display';

export interface CrmUrlInputValue {
  originalUrl?: string;
  displayUrl?: string;
  input?: string;
}

export interface CrmUrlInputProps extends Omit<
  TextInputProps,
  'value' | 'defaultValue' | 'onChange' | 'rightSection' | 'icon' | 'classNames'
> {
  value?: CrmUrlInputValue;
  defaultValue?: CrmUrlInputValue;
  onChange?(value: CrmUrlInputValue): void;
  onConvertToTrackingLink?(url: string): Promise<TrackingUrl | undefined>;
  withTrackingLink?: boolean;
}

const DEFAULT_VALUE: CrmUrlInputValue = {
  originalUrl: '',
  displayUrl: '',
  input: '',
};

export const isValidCrmUrl = (url: string) => {
  return isURL(url, {
    protocols: ['http', 'https'],
    require_protocol: true,
  });
};

export const CrmUrlInput = ({
  value,
  defaultValue,
  onChange,
  onBlur,
  onKeyDown,
  readOnly,
  withTrackingLink = true,
  onConvertToTrackingLink,
  placeholder,
  disabled,
  ...rest
}: CrmUrlInputProps) => {
  const [converting, setConverting] = useState(false);
  const [_value, handleChange] = useUncontrolled({
    value,
    defaultValue,
    finalValue: DEFAULT_VALUE,
    onChange,
  });

  const { originalUrl = '', displayUrl = '', input = '' } = _value;

  const displayTrackingLink = !!_value.originalUrl;
  const withRemoveTrackingLink = displayTrackingLink && !readOnly && !disabled;
  const _readOnly = displayTrackingLink || readOnly;
  const _disabled = converting || disabled;

  const handleConvertToTrackingLink = async (input: string) => {
    if (_readOnly || !withTrackingLink) return;
    const trimmedInput = input.trim();

    if (!isValidCrmUrl(trimmedInput)) {
      return;
    }

    setConverting(true);
    const ret = await onConvertToTrackingLink?.(trimmedInput);
    setConverting(false);

    if (!ret) {
      return;
    }

    handleChange({
      originalUrl: ret.originalUrl,
      displayUrl: ret.trackingUrl,
      input: '',
    });
  };

  return (
    <TextInput
      {...rest}
      classNames={{
        section:
          'data-[position=left]:pointer-events-auto data-[position=left]:w-[calc(100%-32px)] data-[position=left]:justify-start data-[position=left]:pl-2 data-[position=left]:pr-1',
      }}
      placeholder={displayTrackingLink ? undefined : placeholder}
      leftSection={
        displayTrackingLink && <TrackingLinkDisplay target={originalUrl} title={displayUrl} />
      }
      rightSection={
        withRemoveTrackingLink && (
          <CloseButton
            onClick={() => {
              handleChange(DEFAULT_VALUE);
            }}
          />
        )
      }
      readOnly={_readOnly}
      disabled={_disabled}
      value={input}
      onChange={(e) => {
        handleChange({
          displayUrl: '',
          originalUrl: '',
          input: e.currentTarget.value,
        });
      }}
      onBlur={(e) => {
        handleConvertToTrackingLink(input);
        onBlur?.(e);
      }}
      onKeyDown={(e) => {
        if (e.key === ' ') {
          handleConvertToTrackingLink(input);
        }
        onKeyDown?.(e);
      }}
    />
  );
};
